// Content config for SEO theme landing pages, "coming soon" guides, and
// info/legal pages. No fake retreat listings or reviews — destination links
// point to real curated escape pages in our catalog.

export const RETREAT_STYLES = [
  { label: "Yoga Retreats", blurb: "Daily practice, mindful food and time to slow down.", to: "/yoga-retreats", theme: "yoga" },
  { label: "Wellness Retreats", blurb: "Spas, thermal baths and restorative reset weeks.", to: "/wellness-retreats", theme: "spa" },
  { label: "Yacht Retreats", blurb: "Wake up at sea, swim off the deck, sail at sunset.", to: "/yacht-retreats", theme: "yacht" },
  { label: "Yoga Sailing Retreats", blurb: "Sun-salutations on deck between secluded coves.", to: "/yoga-sailing-retreats", theme: "sunset" },
  { label: "Beginner-Friendly Retreats", blurb: "Gentle, welcoming escapes — no experience needed.", to: "/retreats-for-beginners", theme: "meditation" },
  { label: "Luxury Retreats", blurb: "Five-star stays, private guides and quiet indulgence.", to: "/#escapes", theme: "hotel" },
];

export const HOW_IT_WORKS = [
  { title: "Tell us your travel style", text: "Pick your mood, budget and destination — yoga, wellness, sailing or a mix." },
  { title: "Compare retreat options", text: "We surface curated escapes that match, so you can weigh them side by side." },
  { title: "Choose the escape that fits you", text: "Book directly with our trusted partners. No pressure, no markups." },
];

export const WHY_US = [
  { title: "Simple discovery", text: "One place to explore Europe's best yoga, wellness and yacht retreats." },
  { title: "Retreats by travel style", text: "Browse by mood, budget and destination instead of endless tabs." },
  { title: "Useful guides", text: "Plain-English explainers help you choose the right kind of escape." },
  { title: "Beginner-friendly", text: "We explain the jargon so first-timers feel confident booking." },
  { title: "No pressure, just comparison", text: "We're not a booking platform — we help you compare and decide." },
];

export const GUIDES = [
  { slug: "best-yoga-retreats-for-beginners", title: "Best Yoga Retreats for Beginners", theme: "yoga" },
  { slug: "yoga-retreats-vs-wellness-retreats", title: "Yoga Retreats vs Wellness Retreats", theme: "spa" },
  { slug: "what-to-pack-for-a-yoga-retreat", title: "What to Pack for a Yoga Retreat", theme: "meditation" },
  { slug: "best-yoga-sailing-retreats-in-europe", title: "Best Yoga Sailing Retreats in Europe", theme: "yacht" },
];

export const THEME_PAGES = {
  "yoga-retreats": {
    slug: "yoga-retreats",
    eyebrow: "Retreat style",
    h1: "Yoga Retreats in Europe",
    title: "Yoga Retreats | Find Your Perfect Yoga Escape | YogaToYacht",
    description: "Discover yoga retreats across Europe by destination, budget and travel style — from beginner-friendly weeks to deep vinyasa and meditation immersions.",
    theme: "yoga",
    intro: [
      "A yoga retreat is a few days set aside to deepen your practice, slow down and reset — usually combining daily classes, mindful meals and time in nature.",
      "Whether you want a gentle introduction or an intensive vinyasa immersion, YogaToYacht helps you compare retreats by destination, budget and style so you can find the one that fits you.",
    ],
    expect: ["Daily guided yoga and breathwork", "Mindful, often plant-forward meals", "Time for rest, nature and reflection", "Optional workshops and excursions"],
    whoFor: ["First-timers who want a welcoming, structured week", "Regular practitioners wanting to go deeper", "Anyone craving a screen-free reset in beautiful surroundings"],
    destinationIds: ["santorini-yoga", "algarve-yoga", "crete-yoga", "tuscan-villa-yoga", "mallorca-yoga", "iceland-yoga"],
    faqs: [
      { q: "Do I need experience to join a yoga retreat?", a: "Not at all. Many retreats welcome complete beginners and offer gentle classes alongside more advanced sessions." },
      { q: "How long is a typical yoga retreat?", a: "Most run between 3 and 8 days. Shorter weekend resets and longer two-week immersions are also common." },
      { q: "What should I bring?", a: "Comfortable clothing, a refillable water bottle and an open mind. Most venues provide mats and props." },
    ],
    related: ["wellness-retreats", "retreats-for-beginners", "yoga-sailing-retreats"],
  },
  "wellness-retreats": {
    slug: "wellness-retreats",
    eyebrow: "Retreat style",
    h1: "Wellness Retreats in Europe",
    title: "Wellness Retreats | Spa, Thermal & Reset Escapes | YogaToYacht",
    description: "Explore wellness retreats across Europe — spa rituals, thermal baths and restorative reset weeks chosen by destination, budget and travel style.",
    theme: "spa",
    intro: [
      "A wellness retreat focuses on rest and recovery — think spa treatments, thermal bathing, gentle movement and nourishing food, rather than a fixed yoga schedule.",
      "Use YogaToYacht to compare wellness escapes by location and style, from city thermal weekends to slow countryside reset weeks.",
    ],
    expect: ["Spa, sauna and thermal bathing", "Restorative movement and slow mornings", "Healthy, seasonal cuisine", "Space to genuinely switch off"],
    whoFor: ["Anyone feeling burnt out and in need of rest", "Couples or friends wanting a calm escape", "Travellers who prefer spa rituals over structured classes"],
    destinationIds: ["budapest-baths", "vienna-thermae", "crete-spa", "stockholm-sauna", "amsterdam-spa", "rhodes-resort"],
    faqs: [
      { q: "How is a wellness retreat different from a yoga retreat?", a: "Wellness retreats centre on spa, thermal bathing and rest, while yoga retreats build the days around a yoga practice. Many overlap." },
      { q: "Are wellness retreats good for solo travellers?", a: "Yes — they're a relaxing, low-pressure way to travel alone, with plenty of quiet downtime." },
      { q: "Do I need to be fit or flexible?", a: "No. Wellness retreats are about recovery, so most activities are gentle and optional." },
    ],
    related: ["yoga-retreats", "yacht-retreats", "retreats-for-beginners"],
  },
  "yacht-retreats": {
    slug: "yacht-retreats",
    eyebrow: "Retreat style",
    h1: "Yacht Retreats in Europe",
    title: "Yacht Retreats | Sail, Swim & Unwind at Sea | YogaToYacht",
    description: "Discover yacht retreats across the Mediterranean and beyond — sail between coves, swim off the deck and unwind at sea, matched to your travel style and budget.",
    theme: "yacht",
    intro: [
      "A yacht retreat trades the hotel for the open water — you sail between coastlines and coves, swim straight off the deck and watch the sun set from the sea.",
      "YogaToYacht helps you compare sailing-based escapes by region and style, whether you want a lively coastal route or a calm, restorative voyage.",
    ],
    expect: ["Sailing between scenic coves and harbours", "Swimming, snorkelling and deck time", "Sunrise and sunset on the water", "Small-group, sociable atmosphere"],
    whoFor: ["Travellers who love the sea and open skies", "Groups of friends wanting something different", "Anyone after an active-yet-relaxing escape"],
    destinationIds: ["sttropez-yacht", "hvar-croatia", "split-croatia", "mykonos-beachclub", "santorini-sunset", "rhodes-resort"],
    faqs: [
      { q: "Do I need sailing experience for a yacht retreat?", a: "No. Most are crewed or skippered, so you can simply relax while the crew handles the sailing." },
      { q: "Are yacht retreats only for summer?", a: "The Mediterranean season runs roughly May–October, with the warmest waters in mid-summer." },
      { q: "Is there yoga on yacht retreats?", a: "Often, yes — see our yoga sailing retreats for trips that combine deck yoga with sailing." },
    ],
    related: ["yoga-sailing-retreats", "wellness-retreats", "yoga-retreats"],
  },
  "yoga-sailing-retreats": {
    slug: "yoga-sailing-retreats",
    eyebrow: "Retreat style",
    h1: "Yoga Sailing Retreats in Europe",
    title: "Yoga Sailing Retreats | Yoga at Sea in Europe | YogaToYacht",
    description: "Combine daily yoga with sailing on a yoga sailing retreat — practice on deck, anchor in quiet coves and swim in clear water. Compare options by region and budget.",
    theme: "sunset",
    intro: [
      "A yoga sailing retreat blends two escapes into one: morning yoga on deck, then a day of sailing, swimming and exploring secluded bays.",
      "It's the best of both worlds for ocean lovers who also want a daily practice. Compare routes and styles with YogaToYacht.",
    ],
    expect: ["Sunrise or sunset yoga on deck", "Sailing between calm anchorages", "Swimming and snorkelling stops", "A small, like-minded group"],
    whoFor: ["Yogis who love the water", "Couples and friends wanting an active escape", "Anyone after a unique, restorative adventure"],
    destinationIds: ["sttropez-yacht", "santorini-yoga", "hvar-croatia", "mallorca-yoga", "costa-brava-yoga", "split-croatia"],
    faqs: [
      { q: "Is yoga sailing suitable for beginners?", a: "Yes — deck yoga is usually gentle, and no sailing experience is required on crewed trips." },
      { q: "Where do yoga sailing retreats take place?", a: "Popular regions include Greece, Croatia and the western Mediterranean, where calm waters suit on-deck practice." },
      { q: "What if I get seasick?", a: "Routes tend to hug calm coastlines and anchor in sheltered bays, but bring remedies if you're sensitive." },
    ],
    related: ["yacht-retreats", "yoga-retreats", "wellness-retreats"],
  },
  "retreats-for-beginners": {
    slug: "retreats-for-beginners",
    eyebrow: "Retreat style",
    h1: "Beginner-Friendly Retreats",
    title: "Retreats for Beginners | First-Timer Friendly Escapes | YogaToYacht",
    description: "New to retreats? Find beginner-friendly yoga and wellness escapes in Europe with gentle classes, welcoming groups and clear, jargon-free explanations.",
    theme: "meditation",
    intro: [
      "If you've never been on a retreat, the choices can feel overwhelming. Beginner-friendly retreats are designed to be gentle, welcoming and easy to settle into.",
      "We explain what to expect in plain English and highlight escapes that suit first-timers, so you can book with confidence.",
    ],
    expect: ["Gentle, all-levels classes", "Friendly, small groups", "Clear schedules with plenty of free time", "Supportive, unintimidating teachers"],
    whoFor: ["Complete first-timers", "Nervous or solo travellers", "Anyone returning after a long break"],
    destinationIds: ["andalusia-yoga", "algarve-yoga", "crete-yoga", "mallorca-yoga", "lake-garda-yoga", "costa-brava-yoga"],
    faqs: [
      { q: "Will I be the only beginner?", a: "Rarely — beginner-friendly retreats attract lots of first-timers, and classes are taught for all levels." },
      { q: "Do I need to be flexible or fit?", a: "No. You'll be guided at your own pace, and modifications are always offered." },
      { q: "Can I travel solo as a beginner?", a: "Absolutely. Retreats are one of the easiest and safest ways to travel alone and meet people." },
    ],
    related: ["yoga-retreats", "wellness-retreats", "yoga-sailing-retreats"],
  },
};

export const INFO_PAGES = {
  about: {
    slug: "about",
    h1: "About YogaToYacht",
    title: "About | YogaToYacht",
    description: "YogaToYacht helps you discover and compare yoga, wellness and yacht retreats across Europe by destination, budget and travel style.",
    body: [
      { h: null, p: "YogaToYacht is a discovery and comparison site for European retreats. We help you find the right yoga, wellness, sailing or yacht escape based on your mood, budget, destination and travel style." },
      { h: "What we do", p: "We curate retreats from across Europe and organise them by style so they're easy to compare. When you're ready to book, we link you directly to trusted partners. We are not a booking platform and we don't add markups." },
      { h: "Why we built it", p: "Planning a retreat usually means juggling dozens of tabs and confusing jargon. We wanted one calm place to explore options and understand them in plain English — especially for first-timers." },
    ],
  },
  contact: {
    slug: "contact",
    h1: "Contact Us",
    title: "Contact | YogaToYacht",
    description: "Get in touch with the YogaToYacht team with questions, suggestions or partnership enquiries.",
    body: [
      { h: null, p: "We'd love to hear from you — whether you have a question about a retreat style, a suggestion, or a partnership enquiry." },
      { h: "Email", p: "Reach us anytime at hello@yogatoyacht.com and we'll get back to you as soon as we can." },
      { h: "Partnerships", p: "Run retreats or a travel brand? Email us with details and we'll explore how we can feature your offering." },
    ],
  },
  "privacy-policy": {
    slug: "privacy-policy",
    h1: "Privacy Policy",
    title: "Privacy Policy | YogaToYacht",
    description: "How YogaToYacht collects, uses and protects your personal information.",
    body: [
      { h: null, p: "This Privacy Policy explains how YogaToYacht handles your information. By using our site you agree to the practices described here." },
      { h: "Information we collect", p: "We collect the email address you provide when joining our newsletter, along with the page you signed up from and a timestamp. We also collect anonymous usage analytics to improve the site." },
      { h: "How we use it", p: "We use your email only to send occasional retreat ideas and updates. You can unsubscribe at any time. We do not sell your personal data." },
      { h: "Third parties", p: "When you click through to a partner site, that partner's own privacy policy applies. We may earn a referral fee — see our Affiliate Disclosure." },
      { h: "Contact", p: "For any privacy questions or data requests, email hello@yogatoyacht.com." },
    ],
  },
  terms: {
    slug: "terms",
    h1: "Terms of Service",
    title: "Terms of Service | YogaToYacht",
    description: "The terms that govern your use of the YogaToYacht website.",
    body: [
      { h: null, p: "These Terms govern your use of YogaToYacht. By accessing the site, you accept them in full." },
      { h: "Our role", p: "YogaToYacht is a discovery and comparison service. We are not a travel agency or booking platform. Bookings, payments and travel arrangements are made directly with third-party partners under their own terms." },
      { h: "Accuracy", p: "We work to keep information accurate but cannot guarantee that every detail, price or availability shown on partner sites is current. Always confirm details with the provider before booking." },
      { h: "Liability", p: "We are not responsible for the products, services or conduct of third-party partners. Your dealings with them are solely between you and the partner." },
      { h: "Changes", p: "We may update these Terms from time to time. Continued use of the site means you accept the latest version." },
    ],
  },
  "affiliate-disclosure": {
    slug: "affiliate-disclosure",
    h1: "Affiliate Disclosure",
    title: "Affiliate Disclosure | YogaToYacht",
    description: "How YogaToYacht earns referral commissions through affiliate partners, at no extra cost to you.",
    body: [
      { h: null, p: "Transparency matters to us, so here's how YogaToYacht makes money." },
      { h: "How it works", p: "Some links on this site are affiliate links. If you click through to a partner such as TourRadar, Intrepid Travel, BookRetreats or GetYourGuide and make a booking, we may earn a small referral commission — at no extra cost to you." },
      { h: "Our promise", p: "Affiliate relationships never change the price you pay, and they don't influence which retreats we feature. We highlight escapes because we think they're worth your time, not because of commission." },
      { h: "Questions", p: "If you'd like to know more, email hello@yogatoyacht.com." },
    ],
  },
  "how-it-works": {
    slug: "how-it-works",
    h1: "How YogaToYacht Works",
    title: "How It Works | YogaToYacht",
    description: "How YogaToYacht helps you discover and compare yoga, wellness and yacht retreats across Europe — in three simple steps, with no pressure.",
    body: [
      { h: null, p: "YogaToYacht is a discovery and comparison site, not a booking platform. We help you find the right retreat in three simple steps — then you book directly with our trusted partners." },
      { h: "1. Tell us your travel style", p: "Start with what matters to you: your mood, budget, destination and the kind of escape you want — yoga, wellness, sailing, a yacht trip, or a mix of all four." },
      { h: "2. Compare retreat options", p: "We surface curated retreats that match, organised by style so you can weigh them side by side without juggling dozens of browser tabs." },
      { h: "3. Choose the escape that fits you", p: "When you've found the one, you book directly with our partners (TourRadar, Intrepid Travel, BookRetreats, GetYourGuide). We may earn a small referral fee, at no extra cost to you — see our Affiliate Disclosure." },
      { h: "No pressure, just clarity", p: "We don't take bookings or add markups. Our job is simply to help you compare confidently — especially if it's your first retreat." },
    ],
  },
};
